import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ModuleWorkspaceMapPanel } from "@/components/module-map/ModuleWorkspaceMapPanel";
import type { Geometry } from "@/types/api-v2";
import type { RdMapGeometryItem } from "@/types/rd-request";
import type { DrawToolType } from "@/components/map-libre/MapLibreDrawController";

const DRAW_TOOLS: Array<{ id: DrawToolType; title: string }> = [
  { id: "draw-polygon", title: "Полигон" },
  { id: "draw-rectangle", title: "Прямоугольник" },
  { id: "draw-line", title: "Линия" },
  { id: "draw-point", title: "Точка" },
  { id: "draw-freehand", title: "От руки" },
];

function toFeature(drawn: GeoJSON.Feature | GeoJSON.Geometry): GeoJSON.Feature {
  if (drawn.type === "Feature") return drawn;
  return { type: "Feature", properties: {}, geometry: drawn };
}

function rdItemsToOverlayGeometries(items: RdMapGeometryItem[]): Geometry[] {
  const out: Geometry[] = [];
  for (const item of items) {
    if (!item.drawn_geojson) continue;
    const feature = toFeature(item.drawn_geojson);
    const gt = feature.geometry?.type ?? "Polygon";
    const primitive =
      gt === "Point" || gt === "MultiPoint"
        ? "Point"
        : gt === "LineString" || gt === "MultiLineString"
          ? "LineString"
          : "Polygon";
    out.push({
      id: item.request_map_geometry_id,
      object_id: 0,
      name: item.map_geometry_name?.trim() || `Контур РД #${item.request_map_geometry_id}`,
      geojson: feature,
      type: primitive,
      style: {},
      created_at: new Date().toISOString(),
    });
  }
  return out;
}

export type RdWorkspaceMapPanelProps = {
  requestMapGeometries: RdMapGeometryItem[];
  /** Клик по объекту ОИ (MVT) → подстановка в привязку. */
  onOiObjectSelect: (objectId: number, geometryId?: number) => void;
  /** Режим рисования (кнопка «Нарисовать на карте»). */
  drawingActive: boolean;
  onDrawingActiveChange: (active: boolean) => void;
  savePending?: boolean;
  onSaveDrawn: (payload: { name: string; geojson: GeoJSON.Feature }) => void;
  /** Внешний запрос: показать конкретный контур (preview). */
  previewGeometry: Geometry | null;
  /** Увеличивать при каждом запросе preview (в т.ч. повтор того же контура). */
  previewNonce?: number;
  onPreviewConsumed?: () => void;
  /** Свёрнута ли колонка карты (управляется ResizablePanel). */
  collapsed?: boolean;
  onRequestCollapse?: () => void;
  onRequestExpand?: () => void;
};

export function RdWorkspaceMapPanel({
  requestMapGeometries,
  onOiObjectSelect,
  drawingActive,
  onDrawingActiveChange,
  savePending = false,
  onSaveDrawn,
  previewGeometry,
  previewNonce = 0,
  onPreviewConsumed,
  collapsed = false,
  onRequestCollapse,
  onRequestExpand,
}: RdWorkspaceMapPanelProps) {
  const [activeTool, setActiveTool] = useState<string | null>("select");
  const [pendingSketch, setPendingSketch] = useState<GeoJSON.Feature | null>(null);
  const [drawName, setDrawName] = useState("");

  const overlayGeometries = useMemo(
    () => rdItemsToOverlayGeometries(requestMapGeometries),
    [requestMapGeometries],
  );

  const syntheticSelectedObject = useMemo(() => {
    if (overlayGeometries.length === 0) return null;
    return {
      id: 0,
      name: "Контуры заявки РД",
      kls_id: null,
      organization_id: null,
      status: "active",
    };
  }, [overlayGeometries.length]);

  useEffect(() => {
    if (drawingActive) {
      setActiveTool("draw-polygon");
      setPendingSketch(null);
    } else {
      setActiveTool("select");
      setPendingSketch(null);
      setDrawName("");
    }
  }, [drawingActive]);

  const handleDrawingComplete = useCallback((drawing: { geoJSON?: GeoJSON.Feature }) => {
    const f = drawing?.geoJSON;
    if (!f?.geometry) return;
    setPendingSketch(f);
  }, []);

  const handleSave = () => {
    if (!pendingSketch) return;
    const name = drawName.trim() || "Контур заявки";
    onSaveDrawn({ name, geojson: pendingSketch });
    setPendingSketch(null);
    setDrawName("");
    onDrawingActiveChange(false);
  };

  const toolbar = drawingActive ? (
    <>
      <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-border bg-muted/20 px-2 py-1.5">
        {DRAW_TOOLS.map((tool) => (
          <Button
            key={tool.id}
            type="button"
            size="sm"
            variant={activeTool === tool.id ? "default" : "outline"}
            className="h-7 text-xs"
            onClick={() => {
              setActiveTool(tool.id);
              setPendingSketch(null);
            }}
          >
            {tool.title}
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
          onClick={() => onDrawingActiveChange(false)}
        >
          Отмена
        </Button>
      </div>
      {pendingSketch ? (
        <div className="flex shrink-0 flex-wrap items-end gap-2 border-b border-border px-2 py-2">
          <div className="min-w-[140px] flex-1 space-y-1">
            <Label htmlFor="rd-map-draw-name" className="text-xs">
              Название контура
            </Label>
            <Input
              id="rd-map-draw-name"
              className="h-8"
              value={drawName}
              onChange={(e) => setDrawName(e.target.value)}
              placeholder="Название геометрии"
            />
          </div>
          <Button type="button" size="sm" disabled={savePending} onClick={handleSave}>
            {savePending ? "Сохранение..." : "Сохранить в заявку"}
          </Button>
        </div>
      ) : null}
    </>
  ) : null;

  return (
    <ModuleWorkspaceMapPanel
      title="Карта объектов ОИ"
      collapsed={collapsed}
      onRequestCollapse={onRequestCollapse}
      onRequestExpand={onRequestExpand}
      overlayGeometries={overlayGeometries}
      selectedObject={syntheticSelectedObject}
      onMvtSelect={({ objectId, geometryId }) => onOiObjectSelect(objectId, geometryId)}
      previewGeometry={previewGeometry}
      previewNonce={previewNonce}
      onPreviewConsumed={onPreviewConsumed}
      toolbar={toolbar}
      activeTool={drawingActive ? activeTool : "select"}
      onActiveToolChange={setActiveTool}
      isDrawing={drawingActive}
      pendingSketchFeature={pendingSketch}
      onDrawingComplete={handleDrawingComplete}
    />
  );
}
